// Pure helpers for the workflow Report tab (Figma "8. Report"): run-row
// labeling, the report date format, and the Markdown export ("download as PDF
// from Markdown" — we produce the Markdown; the browser's print dialog turns
// the rendered report into a PDF). Unit-tested.

import {
  GENERAL_LEAD_KEYS,
  formatGeneralScalar,
  generalInline,
  generalResultKeysExcluding,
  hasGeneralContent,
  humanizeGeneralKey,
  isScaffoldingKey,
  openQuestionCount,
  resultHasAnswer,
} from './generalResultGuard'
import type { WorkflowRunView } from './workflowTypes'

export type ReportStatusTone = 'clean' | 'attention' | 'failed' | 'running'

export interface ReportStatusPill {
  label: string
  tone: ReportStatusTone
}

const FAILED_RUN_STATUSES = new Set([
  'failed',
  'blocked',
  'guardrail_stopped',
  'budget_limited',
  'cancelled',
])

/** Terminal, non-failure statuses — the ones whose report is worth grading by CONTENT. */
const DELIVERED_RUN_STATUSES = new Set(['completed', 'completed_with_unknowns'])

/**
 * Status pill for a report row: Clean run / Open questions / No result / Failed /
 * Running.
 *
 * For a DELIVERED run the pill grades what the report CONTAINS, not the engine's
 * verdict: an answer with no open questions is a clean run. Genuine stops
 * (failed / blocked / guardrail / budget / cancelled) keep their own pill — a
 * safety stop must never read green — and a non-terminal run still reads Running.
 */
/**
 * Is this narrative a SERIALIZED INTERNAL STRUCTURE rather than reader-facing prose?
 *
 * Run `108301b9` (2026-08-11) put a raw `historianEvidence` blob —
 * `{"evidencePack":[{"id":"ev-safety-triage-1",…}]}` — under "Analysis (unstructured)", captioned
 * "Review before relying on it". A structure is not something a reader can review; asking them to is
 * the caveat admitting it should not be shown. Genuine parse-fail PROSE is kept, so a real analysis
 * is never silently lost.
 *
 * ONE DEFINITION, imported by both consumers. The panel and the Markdown export must agree about
 * this: two copies drift the moment either is tightened, and the failure is silent — a blob reaching
 * the export while the panel hides it looks like an export bug, not a filter bug.
 *
 * TWO TESTS, and the second exists because the first cannot be relied on alone:
 *
 *  · it PARSES as JSON — the whole, well-formed case;
 *  · or it OPENS like one (`{"` / `[{`) — the TRUNCATED case. These narratives are captured from a
 *    step whose output failed to parse and are size-capped (`108301b9`: `text_chars=2123`), so a
 *    cut-off dump is a realistic shape and it never parses.
 *
 * The opening test is deliberately narrower than "starts with a bracket", because prose really does:
 * the doc composer emits `[TABLE on page 24]: The image is a section of…`. `[T` is not `[{`, so that
 * survives — which is also why the parse test cannot simply be dropped in favour of this one.
 */
export function isSerializedStructureBlob(text: string): boolean {
  const trimmed = text.trim()
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return false
  if (/^[[{]\s*[[{"]/.test(trimmed)) return true
  try {
    JSON.parse(trimmed)
    return true
  } catch {
    return false
  }
}

export function reportStatusPill(
  run: Pick<WorkflowRunView, 'status' | 'finalOutput'>,
): ReportStatusPill {
  if (FAILED_RUN_STATUSES.has(run.status)) return { label: 'Failed', tone: 'failed' }
  if (!DELIVERED_RUN_STATUSES.has(run.status)) return { label: 'Running', tone: 'running' }
  const output = asRecord(run.finalOutput) ?? undefined
  // Open questions FIRST: when the report renders blocked claims, that is the
  // honest label whether or not findings accompany them (the reader can point at
  // them). "No result" is reserved for a report with nothing to show at all.
  if (openQuestionCount(output) > 0) return { label: 'Open questions', tone: 'attention' }
  if (!resultHasAnswer(output)) return { label: 'No result', tone: 'attention' }
  return { label: 'Clean run', tone: 'clean' }
}

/**
 * Scheduled vs Manual (design's "Run type" column). There is no scheduler yet,
 * so runs are Manual unless their input carries a schedule trigger marker —
 * the seam the scheduler will use.
 */
export function runTypeLabel(run: Pick<WorkflowRunView, 'input'>): 'Scheduled' | 'Manual' {
  const trigger = (run.input as { trigger?: unknown } | null | undefined)?.trigger
  return trigger === 'schedule' ? 'Scheduled' : 'Manual'
}

/** "Jul 4, 2026, 09:12" (the design's report date format). */
export function formatReportDate(
  iso: string | null | undefined,
  // Explicit locale param so tests can pin the output — `undefined` follows
  // the viewer's locale in the browser but the PROCESS locale under Node,
  // which made assertions runner-dependent.
  locale?: string,
): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const day = date.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })
  const time = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false })
  return `${day}, ${time}`
}

function str(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

/** Structured half of a source-of-knowledge refusal, or null. */
export type SokRefusalDetail = {
  needed: string[]
  supported: Array<{ blueprintId: string; name: string; discipline: string | null }>
}

/**
 * Read `finalOutput.sokRefusal` field by field. jsonb written by two engines
 * across versions — a shape that does not match is a skip, never a crash.
 */
export function parseSokRefusal(output: Record<string, unknown>): SokRefusalDetail | null {
  const raw = output.sokRefusal
  if (typeof raw !== 'object' || raw === null) return null
  const record = raw as Record<string, unknown>
  const needed = Array.isArray(record.needed)
    ? record.needed.filter((item): item is string => typeof item === 'string' && item.trim() !== '')
    : []
  const supported = Array.isArray(record.supported)
    ? record.supported.flatMap((item) => {
        if (typeof item !== 'object' || item === null) return []
        const entry = item as Record<string, unknown>
        const blueprintId = str(entry.blueprintId)
        const name = str(entry.name) || blueprintId
        if (!blueprintId) return []
        return [{
          blueprintId,
          name,
          // Trim: a padded / whitespace-only discipline must not render a dangling `·`.
          discipline: str(entry.discipline) || null,
        }]
      })
    : []
  return needed.length === 0 && supported.length === 0 ? null : { needed, supported }
}

/**
 * Markdown report from a finished run's artifact. Covers the known
 * troubleshooting-result sections (problem → diagnosis → actions) in plain
 * language; anything else about the artifact stays available in the app.
 */
export function reportMarkdown(run: WorkflowRunView): string {
  const output = asRecord(run.finalOutput) ?? {}
  const lines: string[] = []

  lines.push(`# Workflow report — ${formatReportDate(run.startedAt ?? run.completedAt)}`)
  lines.push('')
  lines.push(`- Status: ${reportStatusPill(run).label}`)
  lines.push(`- Run type: ${runTypeLabel(run)}`)
  lines.push(`- Run id: ${run.id}`)
  lines.push('')

  // OWNERSHIP IS DERIVED, NOT MIRRORED. Every heading below records the fixed key it consumed, and
  // `## Result` dedups against THAT set. A second hand-maintained list (EXPORT_FIXED_SECTION_KEYS) was
  // wrong in both directions, because the export reads DIFFERENT FIELDS than the panel: it renders a
  // `problemStatement` from `.text || .statement` where the panel's predicate wants
  // `observedDeviation | expectedState | …`, `## Diagnosis` from `.statement || .summary`, and actions
  // from `.action || .title || .statement`. So `{ statement: 'low flow' }` rendered twice while
  // `{ observedDeviation: 'low flow' }` was suppressed and appeared NOWHERE (graphite #26064).
  // Recording what was emitted makes ownership exactly "this surface rendered it", with nothing to
  // keep in step when a heading's source field changes.
  const renderedFixedKeys = new Set<string>()

  // Same structured half the panel paints. The export used to keep the flat
  // refusal the screen just stopped showing (greptile P1 #28658). Both surfaces
  // or neither — same rule as remainingUncertainty (#27182).
  const refusal = parseSokRefusal(output)
  if (refusal) {
    renderedFixedKeys.add('sokRefusal')
    if (refusal.needed.length > 0) {
      lines.push('## To support this we would need', '')
      refusal.needed.forEach((item) => lines.push(`- ${item}`))
      lines.push('')
    }
    if (refusal.supported.length > 0) {
      lines.push('## Supported today', '')
      refusal.supported.forEach((item) => {
        lines.push(item.discipline ? `- ${item.name} · ${item.discipline}` : `- ${item.name}`)
      })
      lines.push('')
    }
  }

  const problem = asRecord(output.problemStatement)
  // Split the ARTIFACT sources from the run-input fallback: the heading can be rendered from
  // `run.input.problem`, which says nothing about `output.problemStatement`. Recording ownership then
  // suppressed a `generalResult.problemStatement` whose text the export never printed — a narrow but
  // real drop, and the reason ownership is recorded per SOURCE rather than per heading.
  const problemFromArtifact =
    str(problem?.text) || str(problem?.statement) || str(output.problemStatement)
  const problemText =
    problemFromArtifact || str((run.input as { problem?: unknown } | null)?.problem)
  if (problemText) {
    if (problemFromArtifact) renderedFixedKeys.add('problemStatement')
    lines.push('## Problem statement', '', problemText, '')
  }

  const primary = asRecord(output.primaryCandidate)
  const diagnosis = str(primary?.statement) || str(primary?.summary)
  if (diagnosis) {
    renderedFixedKeys.add('primaryCandidate')
    lines.push('## Diagnosis', '', diagnosis, '')
  }

  // The `title`/`statement` alternates are NOT dead code: #26089 made the assembler always populate
  // `.action`, but only for artifacts built after it — a stored run from before that lands here with its
  // text still under the model's original key, and dropping these falls back to a blank export.
  const actions = asArray(output.actions)
    .map((a) => asRecord(a))
    .map((a) => str(a?.action) || str(a?.title) || str(a?.statement))
    .filter(Boolean)
  if (actions.length > 0) {
    renderedFixedKeys.add('actions')
    lines.push('## Corrective action', '')
    actions.forEach((action) => lines.push(`- ${action}`))
    lines.push('')
  }

  const outcome = asRecord(output.outcome)
  const roots = asArray(outcome?.confirmedRootCauses)
    .map((r) => str(asRecord(r)?.statement))
    .filter(Boolean)
  // `remainingUncertainty` IS NO LONGER EXPORTED, for the same reason the panel stopped rendering it
  // (`OpenQuestionsSection`). It has to go from BOTH surfaces or neither: a caveat the reader cannot see
  // on screen but finds in the downloaded report is a divergence between two views of one run, and the
  // export is the surface with no app to fall back to — so the asymmetry would read as the panel hiding
  // something (greptile on #27182).
  //
  // Its content is carried by the two fields that own it and are still exported: a claim the run could
  // not confirm is a `blockedClaim`, and a judgment made on the reader's behalf is an `assumption`. The
  // field itself remains on the artifact and in `uncertainty=` telemetry — #27150 filters the engine
  // bookkeeping that used to dominate it, and that division of labour is deliberate: #27150 owns what
  // the artifact and the metric SAY, this owns what a reader SEES.
  // Link-4: an empty structured outcome can still carry a grounded prose analysis
  // (`analysisNarratives`, from a parse-failed synthesis step). Note it in the
  // summary so the exported/emailed report doesn't read as empty when findings exist.
  // Drop serialized structures (the `108301b9` shape) — the SAME predicate the result panel uses, so
  // the page and the export cannot disagree about what counts as prose.
  const narratives = asArray(output.analysisNarratives)
    .map((n) => asRecord(n))
    .map((n) => ({ step: str(n?.step), text: str(n?.text) }))
    .filter((n) => n.text !== '' && !isSerializedStructureBlob(n.text))
  if (roots.length > 0 || narratives.length > 0) {
    // `## Summary` is built from the outcome's ROOT CAUSES (and a narratives pointer), so it only owns
    // `outcome` when the outcome itself contributed — a `probableHypotheses`-only outcome renders
    // nothing here and must NOT be deduped.
    if (roots.length > 0) renderedFixedKeys.add('outcome')
    lines.push('## Summary', '')
    roots.forEach((root) => lines.push(`- Root cause: ${root}`))
    if (roots.length === 0 && narratives.length > 0) {
      lines.push('- Grounded analysis available (no root cause formally confirmed) — see Analysis below.')
    }
    lines.push('')
  }

  // THE MEASUREMENT-SCALE WARNING, on both surfaces or neither — the rule this file already applies
  // to the SoK refusal and to `remainingUncertainty` (#27182). The panel paints `basisOmitted`; the
  // export dropped it, so a downloaded or forwarded report carried the invalid comparison with none
  // of the warning that it is invalid (greptile P1 on #30620). The export is the one surface with
  // no app to fall back to, which is the same argument `## Analysis` below is here for.
  const basisNotes = asArray(output.basisOmitted)
    .map((note) => (typeof note === 'string' ? note.trim() : ''))
    .filter((note) => note !== '')
  if (basisNotes.length > 0) {
    renderedFixedKeys.add('basisOmitted')
    lines.push('## Measurement basis', '')
    basisNotes.forEach((note) => lines.push(`- ${note}`))
    lines.push('')
  }

  // …and RENDER that analysis. The summary line above points the reader here, so omitting the
  // section made a downloaded or emailed report cite findings the reader cannot reach — the export
  // is the one surface with no app to fall back to (greptile #25976). Rendered whenever narratives
  // exist, not only in the no-root-cause case: a run with a confirmed cause AND a degraded phase
  // would otherwise still drop that phase's prose from the export.
  if (narratives.length > 0) {
    renderedFixedKeys.add('analysisNarratives')
    lines.push('## Analysis', '')
    narratives.forEach(({ step, text }) => {
      // `step` is the source output key — kept as the sub-heading so a multi-phase degraded run is
      // attributable, and skipped when absent rather than printing an empty heading.
      if (step) lines.push(`### ${step}`, '')
      lines.push(text, '')
    })
  }

  // A general (non-troubleshooting) workflow's DECLARED result (`generalResult`) — e.g. a HAZOP node
  // definition's node/sections/boundaries. The panel renders it; the export must too, or a
  // downloaded/emailed node definition is an empty shell (same lesson as the analysis section).
  // Curated exactly like the panel: the declared output only, internal markers excluded, NEVER raw
  // JSON — so the reverted #26005 dump cannot leak through the export either.
  // Shared key selection — the SAME `generalResultKeys` the panel uses, so the export inherits the
  // fixed-section dedup: a key already rendered above (## Corrective action from `actions`, diagnosis
  // from `primaryCandidate`, …) is not emitted again under ## Result. The export previously filtered
  // on scaffolding + content only, with no dedup, so it printed `actions` twice (graphite #26045).
  const generalResult = asRecord(output.generalResult)
  if (generalResult) {
    const keys = generalResultKeysExcluding(output, [...renderedFixedKeys])
    if (keys.length > 0) {
      // BODY FIRST, heading only if there is one. The content gate (`hasGeneralContent`) recurses to
      // any depth while the renderer flattens a bounded number of levels, so a deep-enough shape
      // ({a:{b:{c:1}}}) passes the gate and renders nothing — a heading over blank space, the very
      // symptom this PR fixes at depth 2. Ordering it this way closes the class structurally: no
      // future depth or shape can title an empty section, and the two predicates no longer have to
      // agree (self-review #26064).
      const sections = keys
        .map((key) => ({ key, body: generalResultMarkdown(generalResult[key], key) }))
        .filter(({ body }) => body.some((line) => line.trim().length > 0))
      if (sections.length > 0) {
        lines.push('## Result', '')
        for (const { key, body } of sections) {
          lines.push(`### ${humanizeGeneralKey(key)}`, '', ...body, '')
        }
      }
    }
  }

  return lines.join('\n')
}

/** Markdown lines for one top-level generalResult value: scalar → a line; array/record → bullets.
 *  Unrenderable depth is omitted, never `JSON.stringify`d (the #26005-dump guard, export side). */
function generalResultMarkdown(value: unknown, key?: string): string[] {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    // `key` so a top-level id-like field (`nodeId: 'hyp_interlock_trigger'`) is printed verbatim
    // rather than humanized into prose.
    return [formatGeneralScalar(value, key)]
  }
  const arr = asArray(value)
  if (arr.length) {
    return arr
      .map((item) => {
        if (typeof item !== 'object' || item === null) return `- ${formatGeneralScalar(item)}`
        const rec = asRecord(item)
        if (!rec) return ''
        const leadKey = GENERAL_LEAD_KEYS.find((k) => str(rec[k]))
        const lead = leadKey ? str(rec[leadKey]) : ''
        const rest = Object.entries(rec)
          .filter(([k, v]) => k !== leadKey && !isScaffoldingKey(k) && hasGeneralContent(v))
          .map(([k, v]) => `${humanizeGeneralKey(k)}: ${generalInline(v, k)}`)
          .filter((s) => !s.endsWith(': '))
        const head = lead || rest.shift() || ''
        if (!head) return ''
        return [`- ${head}`, ...rest.map((r) => `  - ${r}`)].join('\n')
      })
      .filter(Boolean)
  }
  const rec = asRecord(value)
  if (rec) {
    return Object.entries(rec)
      .filter(([k, v]) => !isScaffoldingKey(k) && hasGeneralContent(v))
      .map(([k, v]) => `- ${humanizeGeneralKey(k)}: ${generalInline(v, k)}`)
      .filter((s) => !s.endsWith(': '))
  }
  return []
}

/** Trigger a client-side download of `text` as `filename`. */
export function downloadTextFile(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  try {
    anchor.click()
  } finally {
    // Always release the blob URL — a throwing click() must not leak it.
    anchor.remove()
    URL.revokeObjectURL(url)
  }
}
